/**
 * Chat Context Tracker - Phase 1
 * Detects page context and user behavior for intelligent chat triggers
 * Version 2.8.0 - Enhanced with session memory
 */

import type { LastSessionData } from './session-memory'

// Captured when the browser evaluates this module, i.e. at page entry.
// timeOnPage is derived from this on demand (at message-send time) instead
// of a permanent 1s interval re-rendering the chat shell — the value is
// only ever consumed when a chat message is composed. (SSR evaluation of
// this line is harmless; the value is only read in the browser.)
const pageEnteredAt = Date.now()

/** Whole seconds since page entry. Same wall-clock semantics the old
 * ChatWidget interval produced, computed only when needed. */
export function getElapsedSecondsOnPage(): number {
  return Math.max(0, Math.floor((Date.now() - pageEnteredAt) / 1000))
}

export type PageType =
  | 'home'
  | 'about'
  | 'services'
  | 'portfolio'
  | 'blog'
  | 'pricing'
  | 'contact'
  | 'vector'
  | 'hive'
  | 'radar-sites'
  | 'other'

export interface ChatContext {
  // Page information
  pageType: PageType
  pageUrl: string
  pageTitle: string
  serviceName?: string // For service pages
  projectName?: string // For portfolio pages
  blogTopic?: string // For blog pages

  // Timing
  timeOnPage: number // seconds
  timestamp: string

  // User journey
  referrer: string
  isReturnVisitor: boolean
  previouslyEngaged: boolean

  // Device
  userAgent: string
  isMobile: boolean
}

export interface TriggerRules {
  pageType: PageType
  delaySeconds: number
  message: string
  enableExitIntent: boolean
  highIntent: boolean // True for pricing, contact, services pages
}

/**
 * Detect page type from URL pathname
 */
export function detectPageType(pathname: string): PageType {
  if (pathname === '/') return 'home'
  if (pathname.startsWith('/about')) return 'about'
  if (pathname.startsWith('/services')) return 'services'
  if (pathname.startsWith('/portfolio') || pathname.startsWith('/projects'))
    return 'portfolio'
  if (pathname.startsWith('/blog')) return 'blog'
  if (pathname.startsWith('/pricing')) return 'pricing'
  if (pathname.startsWith('/contact')) return 'contact'
  if (pathname.startsWith('/vector')) return 'vector'
  if (pathname.startsWith('/hive')) return 'hive'
  if (pathname.startsWith('/radar-sites')) return 'radar-sites'
  return 'other'
}

/**
 * Extract service name from service page URL
 */
export function extractServiceName(pathname: string): string | undefined {
  if (!pathname.startsWith('/services/')) return undefined

  const servicePart = pathname.replace('/services/', '').split('/')[0]
  // Convert slug to readable name
  return servicePart
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Extract project name from portfolio URL
 */
export function extractProjectName(pathname: string): string | undefined {
  if (!pathname.startsWith('/projects/') && !pathname.startsWith('/portfolio/'))
    return undefined

  const projectPart = pathname
    .replace('/projects/', '')
    .replace('/portfolio/', '')
    .split('/')[0]

  return projectPart
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Extract blog topic from blog URL (simple keyword extraction)
 */
export function extractBlogTopic(pathname: string): string | undefined {
  if (!pathname.startsWith('/blog/')) return undefined

  const blogSlug = pathname.replace('/blog/', '').split('/')[0]
  // Take first 2-3 words as topic
  const words = blogSlug.split('-').slice(0, 3)
  return words.join(' ')
}

/**
 * Get current page context
 */
export function getCurrentContext(): ChatContext {
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : '/'
  const pageType = detectPageType(pathname)

  return {
    pageType,
    pageUrl: pathname,
    pageTitle: typeof document !== 'undefined' ? document.title : '',
    serviceName: extractServiceName(pathname),
    projectName: extractProjectName(pathname),
    blogTopic: extractBlogTopic(pathname),
    timeOnPage: getElapsedSecondsOnPage(), // Stamped fresh at send time in useVectorChat
    timestamp: new Date().toISOString(),
    referrer:
      typeof document !== 'undefined' ? document.referrer : 'direct/unknown',
    isReturnVisitor: checkReturnVisitor(),
    previouslyEngaged: checkPreviousEngagement(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    isMobile:
      typeof navigator !== 'undefined'
        ? /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
        : false,
  }
}

/**
 * Check if user has visited before
 */
function checkReturnVisitor(): boolean {
  if (typeof localStorage === 'undefined') return false
  return localStorage.getItem('pixelmojo_visited') === 'true'
}

/**
 * Mark user as visited
 */
export function markAsVisited(): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem('pixelmojo_visited', 'true')
}

/**
 * Check if user has engaged with chat before
 */
function checkPreviousEngagement(): boolean {
  if (typeof localStorage === 'undefined') return false
  return localStorage.getItem('pixelmojo_chat_engaged') === 'true'
}

/**
 * Mark user as having engaged with chat
 */
export function markAsEngaged(): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem('pixelmojo_chat_engaged', 'true')
}

/**
 * Get trigger rules based on page type
 * Optimized timing for faster engagement:
 * - High-intent pages: 8-10s
 * - Medium-intent pages: 12-15s
 * - Low-intent pages: 18-20s
 */
export function getTriggerRules(pageType: PageType): TriggerRules {
  const rules: Record<PageType, TriggerRules> = {
    home: {
      pageType: 'home',
      delaySeconds: 18, // Reduced from 30s
      message:
        "Hey! 👋 I'm here to help you explore how AI-native design can accelerate your product.\n\nWhat's the biggest challenge you're facing right now?",
      enableExitIntent: true,
      highIntent: false,
    },
    about: {
      pageType: 'about',
      delaySeconds: 15, // Reduced from 20s
      message:
        'Hi there! 👋 Learning about how we work?\n\nWhat made you curious about Pixelmojo?',
      enableExitIntent: false,
      highIntent: false,
    },
    services: {
      pageType: 'services',
      delaySeconds: 10, // Reduced from 20s - high intent
      message:
        'Hey! 👋 Exploring our services?\n\nWhat problem are you trying to solve?',
      enableExitIntent: true,
      highIntent: true, // High-intent: user exploring solutions
    },
    portfolio: {
      pageType: 'portfolio',
      delaySeconds: 15, // Reduced from 25s
      message:
        "Hey! 👋 Checking out our work?\n\nWhich project caught your eye, or is there a specific industry/type you're interested in?",
      enableExitIntent: true,
      highIntent: false,
    },
    blog: {
      pageType: 'blog',
      delaySeconds: 20, // Reduced from 30s
      message:
        "Hi! 👋 Enjoying the article?\n\nAnything you'd like to explore further?",
      enableExitIntent: false,
      highIntent: false,
    },
    pricing: {
      pageType: 'pricing',
      delaySeconds: 8, // Reduced from 15s - very high intent
      message:
        'Hi! 👋 Exploring pricing options?\n\nWhat type of project are you planning - MVP, branding, growth system, or something else?',
      enableExitIntent: true,
      highIntent: true, // High-intent: user evaluating cost
    },
    contact: {
      pageType: 'contact',
      delaySeconds: 5, // Keep at 5s - highest intent
      message:
        "Hey! 👋 Ready to chat about your project?\n\nWhat's driving this conversation right now - is it an upcoming launch, competitor pressure, or something else?",
      enableExitIntent: false,
      highIntent: true, // High-intent: user ready to reach out
    },
    vector: {
      pageType: 'vector',
      delaySeconds: 10, // Reduced from 20s - high intent product page
      message:
        "Hey! 👋 I'm Vector—the AI agent you're learning about.\n\nWant to see how 12-dimensional qualification works in real-time? Go ahead, ask me anything about your sales process or qualification challenges.",
      enableExitIntent: true,
      highIntent: true, // High-intent: user exploring Vector product
    },
    hive: {
      pageType: 'hive',
      delaySeconds: 10, // High intent - multi-agent product page
      message:
        'Hey! 👋 Exploring Hive?\n\nI can help you understand how multi-agent orchestration works. Are you managing 3+ AI touchpoints already, or planning to scale to that level?',
      enableExitIntent: true,
      highIntent: true, // High-intent: user exploring Hive product
    },
    'radar-sites': {
      pageType: 'radar-sites',
      delaySeconds: 10, // High intent - product page
      message:
        'Hey! 👋 Looking at Radar Sites?\n\nIt is in private preview, so I can tell you what it does today and put you on the early access list. What are you hoping to build?',
      enableExitIntent: true,
      // The page's own conversion is the early access form, so the agent's
      // job here is to answer questions and hand people to it.
      highIntent: true,
    },
    other: {
      pageType: 'other',
      delaySeconds: 20, // Reduced from 30s
      message: 'Welcome to Pixelmojo! 👋\n\nWhat brings you here today?',
      enableExitIntent: false,
      highIntent: false,
    },
  }

  return rules[pageType]
}

/**
 * Generate context-aware greeting message
 * New consultative approach: Short, page-aware, single calibrated question
 * v2.8.0: Enhanced with session memory for return visitors
 */
export function generateGreeting(
  context: ChatContext,
  lastSessionData?: LastSessionData | null
): string {
  const { pageType, serviceName, projectName, blogTopic, isReturnVisitor } =
    context

  // Return visitors with session history get highly personalized greeting
  if (isReturnVisitor && context.previouslyEngaged && lastSessionData) {
    const { topics, servicesDiscussed, budgetRange, timeline, projectType } =
      lastSessionData

    // Build personalized greeting
    const greetingParts: string[] = ['Welcome back! 👋']

    // Mention services discussed
    if (servicesDiscussed.length > 0) {
      const serviceList =
        servicesDiscussed.length === 1
          ? servicesDiscussed[0]
          : servicesDiscussed.slice(0, 2).join(' and ')
      greetingParts.push(`\n\nLast time you explored our ${serviceList}.`)
    }
    // Or mention topics if no specific services
    else if (topics.length > 0) {
      const topicList =
        topics.length === 1 ? topics[0] : topics.slice(0, 2).join(' and ')
      greetingParts.push(`\n\nLast time we discussed ${topicList}.`)
    }

    // Add context about what they were planning
    const contextParts: string[] = []
    if (projectType) contextParts.push(`your ${projectType} project`)
    if (timeline) contextParts.push(`${timeline} timeline`)
    if (budgetRange) contextParts.push(`${budgetRange} budget range`)

    if (contextParts.length > 0 && contextParts.length <= 2) {
      greetingParts.push(`We covered ${contextParts.join(' and ')}.`)
    }

    // Add call-to-action
    greetingParts.push(
      '\n\nDid you want to continue that conversation, or explore something new?'
    )

    return greetingParts.join(' ')
  }

  // Return visitors without session data get basic personalized greeting
  if (isReturnVisitor && context.previouslyEngaged) {
    return 'Welcome back! 👋\n\nDid you have more questions about your project?'
  }

  // Context-aware greetings per page type (consultative, not menu-driven)
  switch (pageType) {
    case 'home':
      return "Hey! 👋 I'm here to help you explore how AI-native design can accelerate your product.\n\nWhat's the biggest challenge you're facing right now?"

    case 'about':
      return 'Hi there! 👋 Learning about how we work?\n\nWhat made you curious about Pixelmojo?'

    case 'services':
      if (serviceName) {
        return `Hey! 👋 Interested in ${serviceName}?\n\nWhat problem are you trying to solve with this?`
      }
      return 'Hey! 👋 Exploring our services?\n\nWhat problem are you trying to solve with this?'

    case 'portfolio':
      if (projectName) {
        return `Hey! 👋 Checking out ${projectName}?\n\nWhich aspect of this project caught your eye?`
      }
      return "Hey! 👋 Checking out our work?\n\nWhich project caught your eye, or is there a specific industry/type you're interested in?"

    case 'blog':
      if (blogTopic) {
        return `Hi! 👋 Enjoying the article?\n\nAnything about ${blogTopic} you'd like to explore further?`
      }
      return "Hi! 👋 Enjoying the article?\n\nAnything you'd like to explore further?"

    case 'pricing':
      return 'Hi! 👋 Exploring pricing options?\n\nWhat type of project are you planning - MVP, branding, growth system, or something else?'

    case 'contact':
      return "Hey! 👋 Ready to chat about your project?\n\nWhat's driving this conversation right now - is it an upcoming launch, competitor pressure, or something else?"

    case 'vector':
      return "Hey! 👋 I'm Vector—the AI agent you're learning about.\n\nWant to see how 12-dimensional qualification works in real-time? Go ahead, ask me anything about your sales process or qualification challenges."

    case 'hive':
      return 'Hey! 👋 Exploring Hive?\n\nI can help you understand how multi-agent orchestration works. Are you managing 3+ AI touchpoints already, or planning to scale to that level?'

    case 'radar-sites':
      return 'Hey! 👋 Looking at Radar Sites?\n\nIt is in private preview, so I can tell you what it does today and put you on the early access list. What are you hoping to build?'

    default:
      return 'Welcome to Pixelmojo! 👋\n\nWhat brings you here today?'
  }
}

/**
 * Exit intent detection
 */
export function detectExitIntent(onExitIntent: () => void): () => void {
  if (typeof document === 'undefined') return () => {}

  let triggered = false

  const handleMouseLeave = (e: MouseEvent) => {
    // Check if mouse is leaving from top of viewport
    if (e.clientY <= 0 && !triggered) {
      triggered = true
      onExitIntent()
    }
  }

  document.addEventListener('mouseleave', handleMouseLeave)

  // Return cleanup function
  return () => {
    document.removeEventListener('mouseleave', handleMouseLeave)
  }
}

/**
 * Format context for AI system prompt
 */
export function formatContextForAI(context: ChatContext): string {
  return `
===== CURRENT PAGE CONTEXT =====
- Page Type: ${context.pageType}
- URL: ${context.pageUrl}
- Page Title: ${context.pageTitle}
${context.serviceName ? `- Service: ${context.serviceName}` : ''}
${context.projectName ? `- Project Viewed: ${context.projectName}` : ''}
${context.blogTopic ? `- Blog Topic: ${context.blogTopic}` : ''}
- Time on Page: ${context.timeOnPage} seconds
- Referrer: ${context.referrer}
- Return Visitor: ${context.isReturnVisitor ? 'Yes' : 'No'}
- Previously Engaged: ${context.previouslyEngaged ? 'Yes' : 'No'}
- Device: ${context.isMobile ? 'Mobile' : 'Desktop'}

🚨 CRITICAL: CONVERSATION APPROACH (MUST FOLLOW IN EVERY RESPONSE)

YOU MUST reference the current page context in your responses. This is not optional.

EXAMPLES OF PAGE-AWARE RESPONSES:
- Services page: "I see you're looking at our [Service Name] service - what specific challenge are you trying to solve?"
- Portfolio: "Since you're checking out our [Project Name] work, are you looking for something similar?"
- Blog: "Based on this article about [Topic], are you facing similar challenges in your business?"
- Pricing: "I see you're exploring our pricing - which service are you most interested in?"
- Contact: "Great that you're on the contact page! What's driving this decision right now?"

RULES:
1. ALWAYS acknowledge the page they're on in your first 1-2 sentences
2. Connect your advice directly to what they're currently viewing
3. Use phrases like "I see you're on...", "Since you're looking at...", "Based on the [page]..."
4. If they ask something unrelated, gently reference back to the current page context
5. Be EXPLICIT about page awareness - don't be subtle about it

This shows the user you understand their journey and makes conversations feel personalized.
`.trim()
}
